<template>
  <div class="rule-selector-wrapper">
    <!-- Multi-select mode: vue-multiselect -->
    <multiselect
      v-if="multiple"
      :value="value"
      :options="rules"
      :multiple="true"
      :close-on-select="false"
      :clear-on-select="false"
      :preserve-search="true"
      track-by="id"
      label="name"
      placeholder="Search and select rules…"
      :disabled="disabled"
      :custom-label="truncateRuleName"
      @input="$emit('input', $event)"
    >
      <template slot="option" slot-scope="{ option }">
        <span :title="option.name">{{ truncateRuleName(option) }}</span>
      </template>
      <template slot="noResult">No matching rules found</template>
      <template slot="noOptions">No rules available</template>
    </multiselect>

    <!-- Single-select mode: original b-select -->
    <b-select
      v-else
      :value="value"
      @input="$emit('input', $event)"
      placeholder="Select rule"
      :disabled="disabled"
      expanded
      class="rule-select"
    >
      <option value="">-- Select a rule --</option>
      <option
        v-for="rule in availableRules"
        :key="rule.id || rule.name"
        :value="rule"
        :title="rule.name"
      >
        {{ truncateLabel(rule.name) }}
      </option>
    </b-select>
  </div>
</template>

<script>
import Multiselect from "vue-multiselect";

export default {
  name: "RuleSelector",
  components: { Multiselect },
  props: {
    value: {
      default: null
    },
    rules: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    availableRules() {
      const ruleList = [...this.rules];
      // If a single rule is selected but not in the list, add it
      if (!this.multiple && this.value && this.value.id) {
        const isAlreadyIncluded = ruleList.some(r => r.id === this.value.id);
        if (!isAlreadyIncluded) {
          ruleList.push(this.value);
        }
      }
      return ruleList;
    }
  },
  methods: {
    // Used as :custom-label — receives the full option object
    truncateRuleName(option) {
      const name = option && option.name ? option.name : option || "";
      const maxLength = 50;
      if (!name || name.length <= maxLength) return name;
      return name.substring(0, maxLength - 3) + "...";
    },
    // Used in b-select option text (receives a plain string)
    truncateLabel(name) {
      if (!name) return "";
      const maxLength = 50;
      if (name.length <= maxLength) return name;
      return name.substring(0, maxLength - 3) + "...";
    }
  }
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style scoped>
.rule-selector-wrapper {
  width: 100%;
}
.rule-select {
  font-size: 0.85rem;
}
.rule-select select {
  font-size: 0.85rem;
  padding: 4px 8px;
  height: auto;
  min-height: 28px;
}
.rule-select option {
  padding: 4px 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* Tune multiselect to match app styling */
.rule-selector-wrapper .multiselect {
  font-size: 0.85rem;
  min-height: 32px;
}
.rule-selector-wrapper .multiselect__tags {
  min-height: 32px;
  padding: 4px 40px 0 6px;
  border-color: #dbdbdb;
  border-radius: 4px;
}
.rule-selector-wrapper .multiselect__tag {
  background: #3273dc;
  border-radius: 4px;
  font-size: 0.8rem;
}
.rule-selector-wrapper .multiselect__tag-icon::after {
  color: #fff;
}
.rule-selector-wrapper .multiselect__tag-icon:hover {
  background: #1749a3;
}
.rule-selector-wrapper .multiselect__option--highlight {
  background: #3273dc;
}
.rule-selector-wrapper .multiselect__option--selected {
  background: #e8f0fe;
  color: #363636;
  font-weight: 600;
}
.rule-selector-wrapper
  .multiselect__option--selected.multiselect__option--highlight {
  background: #c8d8fa;
}
.rule-selector-wrapper .multiselect__placeholder {
  font-size: 0.85rem;
  color: #aaa;
  padding-top: 2px;
}
.rule-selector-wrapper .multiselect__input {
  font-size: 0.85rem;
}

::v-deep .multiselect--active .multiselect__tags {
  padding-top: 8px !important;
}
</style>
